import { useState } from "react";
import { useTranslation } from "react-i18next";
import { MessageSquarePlus, X } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";

const TYPES = ["bug", "idea", "layout", "other"];

export default function BetaFeedback() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [type, setType] = useState("bug");
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (!message.trim()) return toast.error(t("beta.required"));
    setLoading(true);
    try {
      await api.post("/feedback", { type, message, email, page: window.location.pathname });
      toast.success(t("beta.thanks"));
      setMessage(""); setEmail(""); setType("bug"); setOpen(false);
    } catch {
      toast.error(t("beta.required"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="beta-feedback-button"
        className="fixed bottom-5 left-5 z-40 inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white pl-4 pr-5 py-3 shadow-lg text-sm font-bold uppercase tracking-wide transition-colors">
        <MessageSquarePlus className="w-4 h-4 text-orange-400" /> {t("beta.button")}
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:justify-end p-0 sm:p-6" data-testid="beta-feedback-modal">
          <div className="absolute inset-0 bg-slate-900/40" onClick={() => setOpen(false)} />
          <div className="relative bg-white w-full sm:w-[400px] border border-slate-200 shadow-2xl">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
              <div>
                <h3 className="font-black text-slate-900">{t("beta.title")}</h3>
                <p className="text-xs text-slate-500 mt-0.5">{t("beta.subtitle")}</p>
              </div>
              <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-900" data-testid="beta-close"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-4">
              <div className="flex flex-wrap gap-2">
                {TYPES.map((ty) => (
                  <button key={ty} onClick={() => setType(ty)} data-testid={`beta-type-${ty}`}
                    className={`text-xs font-bold uppercase tracking-wide px-3 py-1.5 border transition-colors ${type === ty ? "border-orange-600 bg-orange-600 text-white" : "border-slate-300 text-slate-600 hover:border-orange-400"}`}>
                    {t(`beta.type_${ty}`)}
                  </button>
                ))}
              </div>
              <Textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={4} placeholder={t("beta.messagePlaceholder")} className="rounded-none" data-testid="beta-message" />
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t("beta.emailLabel")} className="rounded-none" data-testid="beta-email" />
              <button onClick={submit} disabled={loading} className="w-full bg-orange-600 hover:bg-orange-700 disabled:opacity-60 text-white px-5 py-3 font-bold uppercase tracking-wide text-sm transition-colors" data-testid="beta-submit">
                {loading ? t("beta.sending") : t("beta.send")}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
